/**
 * EngineWarmer — declarative warm-on-upload for heavy WASM/AI engines.
 *
 * Problem: tools that need 16-30 MB+ assets (DFN3 model, ONNX vocal-remover,
 * FFmpeg core) load them on the action-button click. On 3g a user stares at
 * "Loading…" for ~95 s before processing starts. Telemetry confirms: 44
 * tool_abandons / 30d on /denoise with d=4-14 s — the "feels stuck on
 * Loading" pattern.
 *
 * Architecture:
 *   1. Tool registers its engines once at script init:
 *        EngineWarmer.register('denoise', [
 *            { name: 'ffmpeg', sizeMB: 30, label: 'FFmpeg', required: true,
 *              init: function(p){ return FFmpegProcessor.init(p); } },
 *            { name: 'dfn3',   sizeMB: 16, label: 'DeepFilterNet3',
 *              init: function(p){ return DeepFilterProcessor.init(p); } },
 *        ]);
 *   2. <x-tool-uploader warm-engines="ffmpeg,dfn3"> on file pick triggers
 *      EngineWarmer.warm(slug, [...]). Both engines load in parallel while
 *      the user reads settings.
 *   3. At process-button click, replace `await DeepFilter.init()` with
 *      `await EngineWarmer.ensureWarm(slug, 'dfn3', onProgress)`. If warm
 *      finished, returns instantly. If still warming, attaches progress.
 *      If never started, starts now (no regression vs. legacy click-to-load).
 *
 * Failure model:
 *   - spec.required:true   → init failure rejects ensureWarm()
 *   - spec.required:false  → init failure resolves with `false`
 *   Failures are NOT cached — caller's next ensureWarm retries (transient
 *   network blips don't permanently sour the engine).
 *
 * Telemetry (single source of truth across every consumer):
 *   feature_use { action:'engine_warm_started', name, sizeMB }
 *   feature_use { action:'engine_warm_done',    name, ms, sizeMB }
 *   feature_use { action:'engine_warm_failed',  name, ms, err }
 */
(function(){
    var registry = {};        // {slug: {name: spec}}
    var warming = {};         // {key: Promise<boolean>}        key = slug+':'+name
    var warmed = {};          // {key: true|false}              true = success cached
    var lastError = {};       // {key: Error}                   last init error (for ensureWarm retrieval)
    var lastProgress = {};    // {key: {ratio, label}}          for late-attached listeners
    var listeners = {};       // {key: [onProgress fn]}

    function key(slug, name){ return slug + ':' + name; }

    function notifyProgress(k, ratio, label, sizeMB){
        lastProgress[k] = { ratio: ratio, label: label, sizeMB: sizeMB || 0 };
        // Wave 9 (2026-06-02): broadcast on the window so tools that did NOT
        // ensureWarm-with-callback can still display warm progress in their
        // own UI without holding a reference to the warmer. Closes the
        // silent ~2-min gap during slow-network ffmpeg-core fetch (108
        // ffmpeg-init failures / 7 d; real users waited 17-113 sec before
        // giving up because nothing in the UI told them work was in flight).
        try {
            window.dispatchEvent(new CustomEvent('engine-warm-progress', {
                detail: { key: k, ratio: ratio, label: label, sizeMB: sizeMB || 0 }
            }));
        } catch (_) {}
        var arr = listeners[k];
        if (!arr) return;
        for (var i = 0; i < arr.length; i++) {
            try { arr[i](ratio, label, sizeMB || 0); } catch(_) {}
        }
    }

    function ae(slug, action, payload){
        try {
            if (typeof window.__ae !== 'function') return;
            var p = { slug: slug, action: action };
            if (payload) for (var k in payload) if (payload.hasOwnProperty(k)) p[k] = payload[k];
            window.__ae('feature_use', p);
        } catch(_) {}
    }

    /**
     * Internal orchestrator. Contract: ALWAYS resolves with a boolean,
     * NEVER rejects. Original error (if any) is stashed on `lastError[k]`
     * for `ensureWarm` to retrieve and re-throw with context.
     *
     * This contract is what makes `warm()` (fire-and-forget) safe without
     * a `.catch()` band-aid: if a Promise can't reject, no rejection can
     * escape. The `required`-engine throw is moved up into `ensureWarm`
     * where the caller actually awaits and can handle it.
     */
    /* ── ПРОГРЕВ БАЙТОВ вместо ПРОГРЕВА ДВИЖКА на слабой памяти ──────────────
       Прогрев по выбору файла существует, чтобы спрятать скачивание десятков
       мегабайт, пока человек читает настройки. Но `spec.init()` не только качает
       — он ИНСТАНЦИРУЕТ, и это отдельные деньги: замер 03.09.2026 (рабочий набор
       дерева процессов браузера) — FFmpeg целиком +188 МБ, из них само
       инстанцирование 104 МБ, а при уже скачанных байтах оно занимает 204 мс.
       На телефоне с 4 ГБ эти сотни мегабайт лежат всё время, пока человек ищет
       второй файл, и складываются с работой тула — так и умирала вкладка у автора
       жалобы 01.09.2026 (Redmi Note 9 Pro): «постоянно перезагружает страницу».
       Поэтому warm() на слабой памяти берёт БАЙТЫ (ради них прогрев и был), а
       инстанс поднимает ensureWarm() — то есть момент, когда движок реально нужен.
       ⚠️ warmed[k] при этом НЕ ставится: иначе ensureWarm() короткой дорогой
       вернёт true, и тул пойдёт работать с непонятым движком. */
    var prefetched = {};   // key → true, байты ядра уже в кэше приложения
    var prefetching = {};  // key → Promise
    function lowMemoryDevice(){
        try { var B = window.__memBudget; return !!(B && typeof B.gb === 'function' && B.gb() <= 4); }
        catch (_) { return false; }
    }

    function startWarming(slug, name, opts){
        var k = key(slug, name);
        if (warmed[k] === true) return Promise.resolve(true);

        var _spec = (registry[slug] || {})[name];
        if (opts && opts.bytesOnly && _spec && typeof _spec.prefetch === 'function') {
            if (prefetched[k]) return Promise.resolve(true);
            if (prefetching[k]) return prefetching[k];
            var _t0 = Date.now();
            ae(slug, 'engine_warm_started', { name: name, sizeMB: _spec.sizeMB || 0, bytesOnly: 1 });
            prefetching[k] = Promise.resolve()
                .then(function(){ return _spec.prefetch(function(ratio){ notifyProgress(k, ratio, _spec.label || name, _spec.sizeMB || 0); }); })
                .then(function(ok){
                    prefetched[k] = !!ok; delete prefetching[k];
                    ae(slug, ok ? 'engine_bytes_warmed' : 'engine_warm_failed', { name: name, ms: Date.now() - _t0, sizeMB: _spec.sizeMB || 0, bytesOnly: 1 });
                    return !!ok;
                }, function(){ delete prefetching[k]; return false; });
            return prefetching[k];
        }

        if (warming[k]) return warming[k];

        var spec = (registry[slug] || {})[name];
        if (!spec) {
            // register() call may still be in __sharedModule queue. Warn
            // but don't reject — caller's ensureWarm() will retry once
            // register lands. Real bug: spec.init never registered at all.
            try { console.warn('[EngineWarmer] no spec for', slug, name); } catch(_) {}
            return Promise.resolve(false);
        }

        var t0 = Date.now();
        ae(slug, 'engine_warm_started', { name: name, sizeMB: spec.sizeMB || 0 });

        var p = (async function(){
            try {
                var ok = await spec.init(function(ratio){
                    notifyProgress(k, ratio, spec.label || name, spec.sizeMB || 0);
                });
                warmed[k] = !!ok;
                // Path A (no exception): spec.init resolved. On `ok=false`
                // we don't have an err object — tag with a distinguishing
                // reason so telemetry separates "swallowed-error" from
                // "threw" cases. Tools whose init() returns false (rather
                // than throwing) should ALSO emit their own detailed
                // failure event before returning — see stem-separator.js
                // preload() for the pattern.
                var payload = { name: name, ms: Date.now() - t0, sizeMB: spec.sizeMB || 0 };
                if (!ok) payload.reason = 'init_returned_false';
                ae(slug, ok ? 'engine_warm_done' : 'engine_warm_failed', payload);
                if (ok) delete lastError[k];
                return !!ok;
            } catch(err) {
                ae(slug, 'engine_warm_failed', {
                    name: name, ms: Date.now() - t0,
                    err: String((err && err.message) || err).substring(0, 120),
                    expected: !!(err && err.expected)
                });
                // Stash for ensureWarm() to re-raise with the original error
                // intact. Cleared on next successful warm.
                lastError[k] = err;
                return false;
            } finally {
                delete warming[k];
                listeners[k] = null;
                lastProgress[k] = null;
            }
        })();

        warming[k] = p;
        return p;
    }

    /**
     * After-startWarming gate used by `ensureWarm`. If the caller actually
     * awaits a required engine and it failed with a NON-expected error,
     * we re-raise so the caller's `.catch()` / `try/await` sees the real
     * cause. `expected:true` errors (SIMD_UNSUPPORTED, WASM_UNSUPPORTED)
     * are already surfaced via `ffmpeg-status` event + `__aeToolError` —
     * the global unhandledrejection listener silences them; we mirror that
     * by NOT re-raising them here either (caller gets clean `false`).
     */
    function maybeThrowRequired(slug, name, ok) {
        if (ok) return ok;
        var spec = (registry[slug] || {})[name];
        if (!spec || !spec.required) return false;
        var err = lastError[key(slug, name)];
        if (err && err.expected) return false;
        // Real failure on a required engine — propagate.
        throw err || new Error('Engine "' + name + '" failed to warm');
    }

    function parseNames(input){
        if (input == null) return null;
        if (Array.isArray(input)) return input.slice();
        if (typeof input === 'string') {
            return input.split(',').map(function(s){ return s.trim(); }).filter(Boolean);
        }
        return null;
    }

    var EngineWarmer = {
        /**
         * Register engines for a tool. Idempotent — re-registering same
         * `name` overwrites prior spec (e.g. when localized labels load
         * after the initial register call).
         *
         * spec: { name, init(progressCb)→Promise<boolean>, sizeMB?, label?, required? }
         *
         * Progress callback signature is `(ratio, label, sizeMB) => void` —
         * sizeMB is the registered spec size (0 when unset). Older 2-arg
         * callers (`(ratio, label) => …`) still work; JS silently ignores the
         * extra positional arg. The third field exists so multi-stage progress
         * UIs can render "Loading DeepFilterNet3 — 7.4 / 16 MB" without each
         * tool maintaining its own size table.
         */
        register: function(slug, engines){
            /* Тулы пишут свои спеки (add-music-to-video, denoise, audio-enhancer …),
               и правило «на слабой памяти греем байты» иначе действовало бы только
               на встроенном спеке — то есть мимо тех, кто его переопределил.
               Поэтому дверь к байтам приклеиваем ЗДЕСЬ, один раз для всех. */
            try {
                if (engines && engines.length) for (var _ei = 0; _ei < engines.length; _ei++) {
                    var _e = engines[_ei];
                    if (_e && _e.name === 'ffmpeg' && typeof _e.prefetch !== 'function' &&
                        window.FFmpegProcessor && typeof window.FFmpegProcessor.prefetch === 'function') {
                        _e.prefetch = function(p){ return window.FFmpegProcessor.prefetch(p); };
                    }
                }
            } catch (_) {}
            if (!slug || !Array.isArray(engines)) return;
            if (!registry[slug]) registry[slug] = {};
            for (var i = 0; i < engines.length; i++) {
                var e = engines[i];
                if (!e || !e.name || typeof e.init !== 'function') continue;
                registry[slug][e.name] = e;
            }
        },

        /**
         * Fire-and-forget warm of registered engines. Names: comma-string,
         * array, or null (= all registered for slug). Already-warm engines
         * skip; in-flight engines attach no extra listener.
         */
        warm: function(slug, names){
            // Fire-and-forget. By contract `startWarming` resolves a
            // boolean and never rejects, so no `.catch()` is required —
            // the contract is enforced at the API boundary, not at the
            // call site. Required-engine failures are surfaced only when
            // a caller explicitly awaits via `ensureWarm()`.
            var list = parseNames(names);
            if (list == null) list = Object.keys(registry[slug] || {});
            for (var i = 0; i < list.length; i++) {
                startWarming(slug, list[i], { bytesOnly: lowMemoryDevice() });
            }
        },

        /**
         * Block-until-warm with optional progress callback. Returns
         * Promise<boolean> — true when engine is loaded, false on optional
         * engine init failure, rejects on required engine init failure.
         *
         * If warming is already in-flight, attaches onProgress to it (also
         * fires immediately with last-known ratio so UI doesn't go blank
         * waiting for the next progress tick). If never started, starts now.
         */
        ensureWarm: function(slug, name, onProgress){
            var k = key(slug, name);
            if (warmed[k] === true) {
                if (onProgress) try { onProgress(1, name); } catch(_) {}
                return Promise.resolve(true);
            }
            // Don't cache failures — let the caller's next ensureWarm
            // retry against a possibly-recovered network.
            if (warmed[k] === false) delete warmed[k];

            if (onProgress) {
                var arr = listeners[k] || (listeners[k] = []);
                arr.push(onProgress);
                var lp = lastProgress[k];
                if (lp) try { onProgress(lp.ratio, lp.label, lp.sizeMB || 0); } catch(_) {}
            }
            // Caller awaits — required-engine non-expected failures must
            // propagate so the caller's UI handler sees the real cause.
            // `maybeThrowRequired` re-raises the original error from
            // `lastError[k]` (stashed inside startWarming) intact.
            return startWarming(slug, name).then(function(ok){
                return maybeThrowRequired(slug, name, ok);
            });
        },

        isWarm: function(slug, name){
            return warmed[key(slug, name)] === true;
        },

        isWarming: function(slug, name){
            return !!warming[key(slug, name)];
        },

        /**
         * Has a spec for `name` been registered against `slug`? Read-only
         * predicate. Lets a generic "register-if-absent" caller (e.g.
         * tool-uploader's BUILTIN spec auto-wire) avoid stomping a tool
         * that already wrote a richer spec at script init.
         */
        isRegistered: function(slug, name){
            return !!(registry[slug] && registry[slug][name]);
        },

        /**
         * Test-only: clear all state. Production code never calls this.
         * Exposed as `_reset` (underscore prefix) — never document for
         * tool authors.
         */
        _reset: function(){
            registry = {}; warming = {}; warmed = {};
            lastProgress = {}; listeners = {};
        }
    };

    EngineWarmer.__isStub = false;
    window.EngineWarmer = EngineWarmer;

    if (typeof window.__drainSharedModule === 'function') {
        try { window.__drainSharedModule('EngineWarmer'); } catch(_) {}
    }
})();
